import {
  Announcements,
  closestCenter,
  defaultDropAnimation,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  MeasuringStrategy,
  Modifier,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { SortableTreeItem } from "./components"
import { sortableTreeKeyboardCoordinates } from "./keyboardCoordinates"
import type { FlattenedItem, SensorContext, TreeItem, TreeItems } from "./types"
import {
  buildTree,
  flattenTree,
  getChildCount,
  getProjection,
  removeChildrenOf,
  removeItem,
  setProperty,
} from "./utilities"

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
}

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ]
  },
  easing: "ease-out",
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    })
  },
}

interface Props {
  collapsible?: boolean
  defaultItems?: TreeItems
  indentationWidth?: number
  indicator?: boolean
  removable?: boolean
  onItemsChange?: (items: TreeItems) => void
  disableDrag?: boolean
  isItemDraggable?: (itemId: string) => boolean
  onItemLabelChange?: (itemId: string, label: string) => void
  onItemUrlChange?: (itemId: string, url: string) => void
  trackOrderPlaceholder?: string
}

export function SortableTree({
  collapsible = true,
  defaultItems = [],
  indicator = true,
  indentationWidth = 60,
  removable,
  onItemsChange,
  disableDrag = false,
  isItemDraggable,
  onItemLabelChange,
  onItemUrlChange,
  trackOrderPlaceholder,
}: Props) {
  const [items, setItems] = useState(() => defaultItems)
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null)
  const [offsetLeft, setOffsetLeft] = useState(0)
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null
    overId: UniqueIdentifier
  } | null>(null)
  const [descendantsCount, setDescendantsCount] = useState<
    Map<UniqueIdentifier, number>
  >(new Map())

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items)
    const collapsedItems = flattenedTree.reduce<UniqueIdentifier[]>(
      (acc, { children, collapsed, id }) => {
        if (collapsed && children.length) {
          console.log(
            `Node ${id} is collapsed and has ${children.length} children`,
          )
          return [...acc, id]
        }
        return acc
      },
      [],
    )

    console.log("Collapsed items:", collapsedItems)

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems,
    )
  }, [activeId, items])

  // 计算后代数量的函数
  const calculateDescendantsCount = (
    treeItems: TreeItems,
    activeNodeId: UniqueIdentifier | null = null,
  ) => {
    const countMap = new Map<UniqueIdentifier, number>()

    // 标记所有已折叠的节点ID
    const getAllCollapsedNodeIds = (items: TreeItems): UniqueIdentifier[] => {
      let collapsedIds: UniqueIdentifier[] = []

      for (const item of items) {
        // 如果当前节点已折叠，添加它的ID
        if (item.collapsed && item.children.length > 0) {
          collapsedIds.push(item.id)
        }

        // 即使当前节点折叠，仍然递归处理其子节点以获取所有折叠ID
        // 这样可以处理嵌套折叠的情况
        if (item.children.length > 0) {
          collapsedIds = [
            ...collapsedIds,
            ...getAllCollapsedNodeIds(item.children),
          ]
        }
      }

      return collapsedIds
    }

    const collapsedNodeIds = getAllCollapsedNodeIds(treeItems)

    console.log("折叠的节点IDs:", collapsedNodeIds)

    // 递归计算后代数量
    function countDescendants(item: TreeItem): number {
      // 如果节点没有子项或已折叠，返回0（折叠节点的后代不计入）
      if (!item.children || item.children.length === 0) {
        countMap.set(item.id, 0)
        return 0
      }

      // 如果当前节点已折叠，其后代不计入
      if (item.collapsed) {
        countMap.set(item.id, 0)
        return 0
      }

      // 创建一个不包含活动节点的子节点列表
      const filteredChildren = item.children.filter(
        (child: TreeItem) => child.id !== activeNodeId,
      )

      let totalDescendants = 0

      for (let i = 0; i < filteredChildren.length; i++) {
        const child = filteredChildren[i]
        // 判断是否是最后一个子节点
        const isLastChild = i === filteredChildren.length - 1

        // 如果子节点已折叠，其后代不计入总数
        const childDescendantsCount = countDescendants(child)

        if (isLastChild) {
          // 最后一个子节点只算自身，不算其后代
          totalDescendants += 1
        } else {
          // 非最后一个子节点，算自身和未折叠的后代
          totalDescendants += 1 + childDescendantsCount
        }
      }

      countMap.set(item.id, totalDescendants)
      return totalDescendants
    }

    // 处理所有顶级项目，排除被拖拽的节点
    const filteredItems = treeItems.filter((item) => item.id !== activeNodeId)

    for (const item of filteredItems) {
      countDescendants(item)
    }

    return countMap
  }

  // 初始化后代数量计算
  useEffect(() => {
    if (!activeId) {
      // 只有在不拖拽时更新所有项目的后代数量
      setDescendantsCount(calculateDescendantsCount(items))
    }
  }, [items, activeId])

  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth,
        )
      : null
  const sensorContext: SensorContext = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  })
  const [coordinateGetter] = useState(() =>
    sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth),
  )
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    }),
  )

  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems],
  )
  const activeItem = activeId
    ? flattenedItems.find(({ id }) => id === activeId)
    : null

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    }
  }, [flattenedItems, offsetLeft])

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up ${active.id}.`
    },
    onDragMove({ active, over }) {
      return getMovementAnnouncement("onDragMove", active.id, over?.id)
    },
    onDragOver({ active, over }) {
      return getMovementAnnouncement("onDragOver", active.id, over?.id)
    },
    onDragEnd({ active, over }) {
      return getMovementAnnouncement("onDragEnd", active.id, over?.id)
    },
    onDragCancel({ active }) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`
    },
  }

  return (
    <DndContext
      accessibility={{ announcements }}
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEndAction}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        {flattenedItems.map(
          ({ id, title, children, collapsed, depth, parentId }) => {
            // 获取该节点的后代数量
            const nodeDescendantsCount = descendantsCount.get(id) || 0

            // 判断节点是否是其父节点的最后一个子节点
            const isLastChild = determineIfLastChild(
              flattenedItems,
              id,
              parentId,
              activeId,
            )

            // 计算父级深度
            let parentDepth = 0

            if (parentId) {
              const parentNode = flattenedItems.find(
                (item) => item.id === parentId,
              )

              if (parentNode) {
                parentDepth = parentNode.depth
              }
            }

            // 判断当前项目是否可以拖拽
            const itemCanDrag = isItemDraggable
              ? isItemDraggable(String(id))
              : !disableDrag

            // 从原始items中找到对应的项目以获取完整信息
            const findOriginalItem = (
              items: TreeItems,
              targetId: UniqueIdentifier,
            ): TreeItem | null => {
              for (const item of items) {
                if (item.id === targetId) return item
                if (item.children.length > 0) {
                  const found = findOriginalItem(item.children, targetId)

                  if (found) return found
                }
              }
              return null
            }

            const originalItem = findOriginalItem(items, id)

            // 计算当前节点有多少个特殊子节点
            const calculateSpecialChildrenCount = (
              nodeId: UniqueIdentifier,
            ): number => {
              const directChildren = flattenedItems.filter(
                (item) => item.parentId === nodeId && item.id !== activeId,
              )

              // 如果只有一个子项
              if (directChildren.length === 1) {
                const onlyChild = directChildren[0]
                const childOriginal = findOriginalItem(items, onlyChild.id)

                // 如果这个唯一的子项不是特殊item，无论其子项的子项有没有特殊元素，都视为没有特殊子元素
                if (childOriginal?.isSpecial !== true) {
                  return 0
                }
              }

              let specialCount = 0

              directChildren.forEach((child, index) => {
                const childOriginal = findOriginalItem(items, child.id)
                const isLastChild = index === directChildren.length - 1

                // 如果是特殊项且不是最后一个子节点，计数+1
                if (childOriginal?.isSpecial === true && !isLastChild) {
                  specialCount += 1
                }

                // 对于非最后子节点，计算其所有后代中的特殊项
                if (
                  !isLastChild &&
                  !child.collapsed &&
                  child.children &&
                  child.children.length > 0
                ) {
                  specialCount += calculateAllSpecialDescendants(child.id)
                }

                // 对于最后子节点，递归计算其非最后路径上的特殊项
                if (
                  isLastChild &&
                  !child.collapsed &&
                  child.children &&
                  child.children.length > 0
                ) {
                  specialCount += calculateSpecialChildrenCount(child.id)
                }
              })

              return specialCount
            }

            // 计算节点所有后代中的特殊项数量
            const calculateAllSpecialDescendants = (
              nodeId: UniqueIdentifier,
            ): number => {
              const directChildren = flattenedItems.filter(
                (item) => item.parentId === nodeId && item.id !== activeId,
              )
              let specialCount = 0

              directChildren.forEach((child) => {
                const childOriginal = findOriginalItem(items, child.id)

                // 所有特殊子节点都计数
                if (childOriginal?.isSpecial === true) {
                  specialCount += 1
                }

                // 递归计算所有后代
                if (
                  !child.collapsed &&
                  child.children &&
                  child.children.length > 0
                ) {
                  specialCount += calculateAllSpecialDescendants(child.id)
                }
              })

              return specialCount
            }

            const specialChildrenCount = calculateSpecialChildrenCount(id)

            // 调试信息
            if (nodeDescendantsCount > 0) {
              console.log(`节点 ${id} 的特殊子节点计算:`, {
                nodeId: id,
                descendantsCount: nodeDescendantsCount,
                specialChildrenCount,
                expectedHeight:
                  (44 + 4) * nodeDescendantsCount + specialChildrenCount * 36,
              })
            }

            return (
              <SortableTreeItem
                key={id}
                id={id}
                value={String(id)}
                title={title}
                depth={id === activeId && projected ? projected.depth : depth}
                indentationWidth={indentationWidth}
                indicator={indicator}
                collapsed={Boolean(collapsed && children.length)}
                onCollapse={
                  collapsible && children.length
                    ? () => handleCollapse(id)
                    : undefined
                }
                onRemove={removable ? () => handleRemove(id) : undefined}
                descendantsCount={nodeDescendantsCount}
                isLastChild={isLastChild}
                parentDepth={parentDepth}
                disableDrag={!itemCanDrag}
                isSpecial={originalItem?.isSpecial}
                url={originalItem?.url}
                specialChildrenCount={specialChildrenCount}
                onLabelChange={(label) =>
                  onItemLabelChange?.(String(id), label)
                }
                onUrlChange={(url) => onItemUrlChange?.(String(id), url)}
                trackOrderPlaceholder={trackOrderPlaceholder}
              />
            )
          },
        )}
        {createPortal(
          <DragOverlay
            dropAnimation={dropAnimationConfig}
            modifiers={indicator ? [adjustTranslate] : undefined}
          >
            {activeId && activeItem ? (
              <SortableTreeItem
                id={activeId}
                depth={activeItem.depth}
                clone
                childCount={getChildCount(items, activeId) + 1}
                value={String(activeId)}
                indentationWidth={indentationWidth}
                descendantsCount={descendantsCount.get(activeId) || 0}
                isLastChild={false}
                parentDepth={
                  activeItem.parentId
                    ? getParentDepth(flattenedItems, activeItem.parentId)
                    : 0
                }
                disableDrag={
                  !(isItemDraggable
                    ? isItemDraggable(String(activeId))
                    : !disableDrag)
                }
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </SortableContext>
    </DndContext>
  )

  function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
    setActiveId(activeId)
    setOverId(activeId)

    const activeItem = flattenedItems.find(({ id }) => id === activeId)

    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: activeId,
      })

      // 更新后代数量状态，排除被拖拽的项目
      setDescendantsCount(calculateDescendantsCount(items, activeId))
    }

    document.body.style.setProperty("cursor", "grabbing")
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(delta.x)
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over?.id ?? null)
  }

  function handleDragEndAction({ active, over }: DragEndEvent) {
    if (!over) {
      resetState()
      return
    }

    const activeItem = flattenedItems.find(({ id }) => id === active.id)
    const overItem = flattenedItems.find(({ id }) => id === over.id)

    if (!activeItem || !overItem) {
      resetState()
      return
    }

    if (projected) {
      const { depth, parentId } = projected

      // 获取所有子项
      const childItems = flattenedItems.filter((item) => {
        let parent = item.parentId

        while (parent !== null) {
          if (parent === active.id) {
            return true
          }
          const parentItem = flattenedItems.find((i) => i.id === parent)

          parent = parentItem ? parentItem.parentId : null
        }
        return false
      })

      // 计算最大相对深度
      const maxRelativeDepth =
        childItems.length > 0
          ? Math.max(
              ...childItems.map((item) => {
                let relDepth = 1
                let currentId = item.parentId

                while (currentId !== active.id && currentId !== null) {
                  relDepth++
                  const parentItem = flattenedItems.find(
                    (i) => i.id === currentId,
                  )

                  currentId = parentItem ? parentItem.parentId : null
                }
                return relDepth
              }),
            )
          : 0

      // 检查是否超过最大深度限制（这里设置为3层）
      if (depth + maxRelativeDepth > 3) {
        resetState()
        return
      }

      // 使用完整的扁平化数据，而不是过滤后的flattenedItems
      const fullFlattenedItems = flattenTree(items)
      const fullActiveIndex = fullFlattenedItems.findIndex(
        ({ id }) => id === active.id,
      )
      const fullOverIndex = fullFlattenedItems.findIndex(
        ({ id }) => id === over.id,
      )

      const newItems = arrayMove(
        fullFlattenedItems,
        fullActiveIndex,
        fullOverIndex,
      ).map((item) => {
        if (item.id === active.id) {
          return {
            ...item,
            depth,
            parentId,
          }
        }

        if (childItems.find((child) => child.id === item.id)) {
          return {
            ...item,
            depth: item.depth + (depth - activeItem.depth),
          }
        }

        return item
      })

      const projectedNewItems = buildTree(newItems)

      // 检查是否拖入到折叠的节点，如果是则自动展开
      let finalItems = projectedNewItems

      if (parentId) {
        // 查找父节点是否处于折叠状态
        const findItemInTree = (
          items: TreeItems,
          targetId: UniqueIdentifier,
        ): TreeItem | null => {
          for (const item of items) {
            if (item.id === targetId) return item
            if (item.children.length > 0) {
              const found = findItemInTree(item.children, targetId)

              if (found) return found
            }
          }
          return null
        }
        const parentItem = findItemInTree(projectedNewItems, parentId)

        if (parentItem && parentItem.collapsed) {
          // 如果父节点是折叠的，则展开它
          finalItems = setProperty(
            projectedNewItems,
            parentId,
            "collapsed",
            () => false,
          )
        }
      }

      setItems(finalItems)
      onItemsChange?.(finalItems)
    }

    resetState()
  }

  function handleDragCancel() {
    resetState()
  }

  function resetState() {
    setOverId(null)
    setActiveId(null)
    setOffsetLeft(0)
    setCurrentPosition(null)

    document.body.style.setProperty("cursor", "")
  }

  function handleRemove(id: UniqueIdentifier) {
    const newItems = removeItem(items, id)

    setItems(newItems)
    onItemsChange?.(newItems)
  }

  function handleCollapse(id: UniqueIdentifier) {
    const newItems = setProperty(items, id, "collapsed", (value) => !value)

    setItems(newItems)
    onItemsChange?.(newItems)
  }

  function getMovementAnnouncement(
    eventName: string,
    activeId: UniqueIdentifier,
    overId?: UniqueIdentifier,
  ) {
    if (overId && projected) {
      if (eventName !== "onDragEnd") {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return
        } else {
          setCurrentPosition({
            parentId: projected.parentId,
            overId,
          })
        }
      }

      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items)),
      )
      const overIndex = clonedItems.findIndex(({ id }) => id === overId)
      const activeIndex = clonedItems.findIndex(({ id }) => id === activeId)
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex)

      const previousItem = sortedItems[overIndex - 1]

      let announcement
      const movedVerb = eventName === "onDragEnd" ? "dropped" : "moved"
      const nestedVerb = eventName === "onDragEnd" ? "dropped" : "nested"

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1]

        announcement = `${activeId} was ${movedVerb} before ${nextItem.id}.`
      } else {
        if (projected.depth > previousItem.depth) {
          announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`
        } else {
          let previousSibling: FlattenedItem | undefined = previousItem

          while (previousSibling && projected.depth < previousSibling.depth) {
            const { parentId }: { parentId: UniqueIdentifier | null } =
              previousSibling

            previousSibling = sortedItems.find(({ id }) => id === parentId)
          }

          if (previousSibling) {
            announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`
          }
        }
      }

      return announcement
    }

    return
  }

  // 判断节点是否是其父节点的最后一个子节点
  function determineIfLastChild(
    items: FlattenedItem[],
    nodeId: UniqueIdentifier,
    parentId: UniqueIdentifier | null,
    activeNodeId: UniqueIdentifier | null,
  ): boolean {
    if (!parentId) return false // 根节点不是任何节点的子节点

    // 获取同一父节点下的所有子节点（排除被拖拽的节点）
    const siblings = items.filter(
      (item) => item.parentId === parentId && item.id !== activeNodeId,
    )

    // 排序后的兄弟节点
    const sortedSiblings = [...siblings].sort((a, b) => a.index - b.index)

    // 如果没有兄弟节点，或者是空数组，则不是最后一个
    if (!sortedSiblings.length) return false

    // 查找当前节点在兄弟节点中的最后一个
    const lastSibling = sortedSiblings[sortedSiblings.length - 1]

    return lastSibling?.id === nodeId
  }

  // 获取父节点的深度
  function getParentDepth(
    items: FlattenedItem[],
    parentId: UniqueIdentifier,
  ): number {
    const parentNode = items.find((item) => item.id === parentId)

    return parentNode ? parentNode.depth : 0
  }
}

const adjustTranslate: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - 25,
  }
}
